import React, { useState, useMemo } from "react";
import { Search, Plus } from "lucide-react";
import DataTable from "react-data-table-component";
import Header from "../Components/Header";
import { leadsStyles } from "../utils/customStyles";
import { useLeadMagnets } from "../hooks/useLeadMagnets";
import { getLeadMagnetColumns } from "../utils/leadMagnetColumns";
import { stripHtml } from "../utils/leadMagnetUtils";
import LeadMagnetModal from "../Components/LeadMagnetModal";
import toast from "react-hot-toast";

const TABLE_STYLES = `
  .lead-magnet-description-wrapper {
    max-height: 3em;
    overflow: hidden;
    position: relative;
  }
  .lead-magnet-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    line-height: 1.5em;
    max-height: 3em;
  }
  .lead-magnet-description p { margin: 0; padding: 0; display: inline; }
  .lead-magnet-description p:not(:last-child)::after { content: " "; }
  .lead-magnet-description ul,
  .lead-magnet-description ol { margin: 0; padding-left: 1.2em; display: inline; list-style: none; }
  .lead-magnet-description li { margin: 0; display: inline; }
  .lead-magnet-description ul li::before { content: "• "; }
  .lead-magnet-description ol { counter-reset: item; }
  .lead-magnet-description ol li { counter-increment: item; display: inline; }
  .lead-magnet-description ol li::before { content: counter(item) ". "; }
  .lead-magnet-description a { color: #60a5fa; text-decoration: underline; }
  .lead-magnet-description strong { font-weight: 600; }
  .lead-magnet-description em { font-style: italic; }
`;

const LeadMagnets = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const { leadMagnets, saveLeadMagnet, deleteLeadMagnet, copyToClipboard } = useLeadMagnets();

  const handleOpenModal = () => {
    setEditingLead(null);
    setIsModalOpen(true);
  };

  const handleEditModal = (lead) => {
    setEditingLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLead(null);
  };

  const handleSaveLeadMagnet = async (data, callbacks) => {
    try {
      await saveLeadMagnet(data, editingLead, callbacks?.onProgress);
    } catch (error) {
      console.error("Error saving lead magnet:", error);
      toast.error("Error saving lead magnet");
      throw error;
    }
  };

  const handleDeleteLeadMagnet = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead magnet?")) return;
    try {
      await deleteLeadMagnet(id);
    } catch (error) {
      console.error("Error deleting lead magnet:", error);
      toast.error("Error deleting lead magnet");
    }
  };

  const columns = getLeadMagnetColumns({
    copyToClipboard,
    onEdit: handleEditModal,
    onDelete: handleDeleteLeadMagnet,
  });

  const filteredLeadMagnets = useMemo(() => {
    if (!searchTerm) return leadMagnets;
    return leadMagnets.filter((lead) =>
      [lead.title, stripHtml(lead.description || ""), lead.uuid].some((val) =>
        val?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, leadMagnets]);

  return (
    <div className="text-white overflow-auto scrollbar-thin">
      <style>{TABLE_STYLES}</style>
      <Header
        PageName="Lead Magnets"
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="h-fit bg-[#081028] px-4 py-2">
        <div className="flex justify-between items-center mb-6 mt-5">
          <h1 className="text-2xl font-semibold">Lead Magnets</h1>
          <button
            onClick={handleOpenModal}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Lead Magnet
          </button>
        </div>

        <div className="bg-[#0b1739] rounded border border-slate-700 overflow-hidden">
          <div className="p-4 py-2 border-b border-slate-700">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-lg font-semibold">All Lead Magnets</h2>
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search lead magnets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-[#0b1739] border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-blue-500 w-full sm:w-64"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <DataTable
              columns={columns}
              data={filteredLeadMagnets}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30, 50]}
              dense
              customStyles={leadsStyles}
              noDataComponent={
                <div className="bg-[#0b1739] w-full text-gray-400 py-8 text-center">
                  No lead magnets found
                </div>
              }
              paginationComponentOptions={{
                rowsPerPageText: "Rows per page:",
                rangeSeparatorText: "of",
                noRowsPerPage: false,
                selectAllRowsItem: false,
              }}
            />
          </div>
        </div>
      </div>

      <LeadMagnetModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveLeadMagnet}
        editData={editingLead}
      />
    </div>
  );
};

export default LeadMagnets;
